import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { maskUrl } from 'src/app/utils/sentry-breadcrumb';

/**
 * ErrorMonitoringInterceptor (D-01..D-04, Phase 17)
 *
 * Captures HTTP 5xx responses on appServiceUrl-scoped requests via
 * Sentry.captureException. Pure side-effect: rethrows the original
 * HttpErrorResponse unchanged so downstream subscribers still receive it.
 *
 * - 4xx (including 401) and network errors (status 0) are NOT captured
 *   (4xx is client-state / validation; 401 is AuthInterceptor's territory;
 *    status 0 is offline / CORS — out of scope per D-01).
 * - When environment.sentry.dsn is null, Sentry.captureException is a no-op.
 * - URL is masked via maskUrl() to redact orderId/paymentId UUIDs.
 */
@Injectable()
export class ErrorMonitoringInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.appServiceUrl)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status >= 500 && err.status < 600) {
          try {
            Sentry.captureException(err, {
              captureContext: {
                extra: {
                  url: maskUrl(req.url),
                  method: req.method,
                  status: err.status,
                },
              },
              mechanism: { type: 'angular.http', handled: false },
            });
          } catch {
            // Never let monitoring failures break the user request.
          }
        }
        return throwError(() => err);
      })
    );
  }
}
